import { motion } from "framer-motion";
import { NOOR } from "@/constants/testIds/noor";
import { fadeUp, trans } from "./motion";
import { BOTTLE_FRONT, BOTTLE_MEXICO, NOOR_RETAIL, NOOR_BOX } from "./bottle";
import { BOTTLES_HIDDEN } from "@/lib/visibility";

const SURFACE = { background: "var(--bg-surface)" };

function GalleryItem({ testId, src, alt, caption, meta, className, fit = "cover", delay = 0 }) {
  return (
    <motion.figure
      data-testid={testId}
      {...fadeUp}
      transition={trans(1, delay)}
      className={`relative overflow-hidden group ${className}`}
      style={SURFACE}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={`w-full h-full transition-transform duration-700 group-hover:scale-[1.03] ${
          fit === "contain" ? "object-contain p-10" : "object-cover object-center"
        }`}
      />
      <figcaption className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
        <span className="overline">{caption}</span>
        {meta && <span className="overline" style={{ color: "var(--text-secondary)" }}>{meta}</span>}
      </figcaption>
    </motion.figure>
  );
}

function GalleryIntro() {
  return (
    <div className="grid grid-cols-12 gap-y-10 md:gap-10 mb-16">
      <div className="col-span-12 lg:col-span-3">
        <p className="overline mb-4">IV · The Object</p>
        <span className="ornament" />
      </div>
      <div className="col-span-12 lg:col-span-7 lg:col-start-5">
        <motion.h2
          {...fadeUp}
          transition={trans(1)}
          className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
          style={{ color: "var(--text-primary)" }}
        >
          Each bottle is a <em className="italic" style={{ color: "var(--accent-rose)" }}>jewelled</em>{" "}
          testimony — never to be repeated.
        </motion.h2>
      </div>
    </div>
  );
}

const ITEMS = [
  {
    testId: "galleryItemHero",
    src: BOTTLE_FRONT,
    alt: "NOOR Tequila Blanco — the vessel, front",
    caption: "01 · The Vessel",
    meta: "750 ml",
    fit: "contain",
    className: "col-span-12 md:col-span-7 md:row-span-2 h-[760px]",
  },
  {
    testId: "galleryItemSealed",
    src: NOOR_BOX,
    alt: "NOOR — six-bottle kraft crate sealed in the house",
    caption: "02 · Sealed in the House",
    meta: "Six per batch",
    delay: 0.1,
    className: "col-span-12 md:col-span-5 h-[370px]",
  },
  {
    testId: "galleryItemBox",
    src: BOTTLE_MEXICO,
    alt: "NOOR — Hecho en México",
    caption: "03 · Hecho en México",
    delay: 0.2,
    className: "col-span-12 md:col-span-5 h-[370px]",
  },
  {
    testId: "galleryItemDisplay",
    src: NOOR_RETAIL,
    alt: "NOOR on display — retail photo",
    caption: "04 · The Hall",
    delay: 0.3,
    className: "col-span-12 md:col-span-7 h-[420px]",
  },
];

function GalleryGrid() {
  return (
    <div className="grid grid-cols-12 gap-y-5 md:gap-6">
      {ITEMS.map((item) => (
        <GalleryItem key={item.testId} {...item} testId={NOOR[item.testId]} />
      ))}
    </div>
  );
}

export default function Gallery() {
  return (
    <section data-testid={NOOR.gallerySection} id="gallery" className="relative py-32 md:py-44">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <GalleryIntro />
        {!BOTTLES_HIDDEN && <GalleryGrid />}
      </div>
    </section>
  );
}
